import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Camera, Image as ImageIcon, Upload, Plus, X, RefreshCw, CreditCard as Edit2, Sparkles, IndianRupee, Shield, ImagePlus, CreditCard as Edit, Youtube, AlertCircle, Tag, Loader, GripVertical, ChevronUp, ChevronDown } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { analyzeImage } from '../lib/openai';
import { validateYouTubeUrl, generateSlug } from '../lib/supabase';
import type { CollectibleType } from './CollectiblesList';
import ImageEditor from './ImageEditor';
import CameraCapture from './CameraCapture';

interface ImageData {
  id: string;
  url: string;
  file?: File;
  type: 'file' | 'url';
  existingId?: string;
}

interface Category {
  id: string;
  name: string;
}

interface FormData {
  type: CollectibleType;
  name: string;
  slug: string;
  description: string;
  price: string;
  youtube_url?: string;
  category_id: string;
  status: 'available' | 'sold' | 'unavailable';
}

function EditCollectible() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [images, setImages] = useState<ImageData[]>([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [formData, setFormData] = useState<FormData>({
    type: 'coin',
    name: '',
    slug: '',
    description: '',
    price: '',
    youtube_url: '',
    category_id: '',
    status: 'available'
  });

  useEffect(() => {
    const checkAdminAndFetchData = async () => {
      try {
        // Check if user is admin
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('Not authenticated');
        }

        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', user.id)
          .single();

        if (!profile?.is_admin) {
          throw new Error('Not authorized');
        }

        setIsAdmin(true);

        // Fetch categories
        const { data: categoriesData, error: categoriesError } = await supabase
          .from('categories')
          .select('*')
          .order('name');

        if (categoriesError) throw categoriesError;
        setCategories(categoriesData || []);

        // If editing existing collectible, fetch it
        if (id) {
          const { data: collectible, error: fetchError } = await supabase
            .from('collectibles')
            .select(`
              *,
              images:collectible_images(
                id,
                image_url,
                order
              ),
              category:categories(
                id,
                name
              )
            `)
            .eq('id', id)
            .single();

          if (fetchError) throw fetchError;
          if (!collectible) throw new Error('Collectible not found');

          setFormData({
            type: collectible.type,
            name: collectible.name,
            slug: collectible.slug,
            description: collectible.description || '',
            price: collectible.price.toString(),
            youtube_url: collectible.youtube_url || '',
            category_id: collectible.category_id || '',
            status: collectible.status
          });

          setImages(
            (collectible.images || [])
              .sort((a, b) => a.order - b.order)
              .map(img => ({
                id: img.id,
                url: img.image_url,
                type: 'url',
                existingId: img.id
              }))
          );
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load collectible');
      } finally {
        setLoading(false);
      }
    };

    checkAdminAndFetchData();
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      name: value,
      slug: prev.slug === generateSlug(prev.name) ? generateSlug(value) : prev.slug
    }));
  };

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      slug: generateSlug(value)
    }));
  };

  const retryAnalysis = async () => {
    if (images.length === 0) {
      setError('Please add at least one image to analyze');
      return;
    }

    try {
      const imageUrl = images[0].url;
      const analysis = await analyzeImage(imageUrl);

      setFormData(prev => ({
        ...prev,
        type: analysis.type || prev.type,
        name: analysis.name || prev.name,
        description: analysis.description || prev.description,
        slug: generateSlug(analysis.name || prev.name),
        price: analysis.suggestedPrice?.toString() || prev.price
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to analyze image');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setSaving(true);
    setError(null);

    try {
      // Validate YouTube URL if provided
      if (formData.youtube_url && !validateYouTubeUrl(formData.youtube_url)) {
        throw new Error('Invalid YouTube URL format. Please use a valid YouTube video URL.');
      }

      // Check if slug is unique
      const { data: existingCollectible } = await supabase
        .from('collectibles')
        .select('id')
        .eq('slug', formData.slug)
        .neq('id', id)
        .maybeSingle();

      if (existingCollectible) {
        throw new Error('This URL slug is already in use. Please choose a different one.');
      }

      // Update collectible
      const { error: updateError } = await supabase
        .from('collectibles')
        .update({
          type: formData.type,
          name: formData.name,
          slug: formData.slug,
          description: formData.description,
          price: parseInt(formData.price, 10),
          youtube_url: formData.youtube_url || null,
          category_id: formData.category_id || null,
          status: formData.status
        })
        .eq('id', id);

      if (updateError) throw updateError;

      // Update order for existing images
      const existingImages = images.filter(img => img.type === 'url' && img.existingId);
      for (let i = 0; i < existingImages.length; i++) {
        const img = existingImages[i];
        if (img.existingId) {
          const { error: orderError } = await supabase
            .from('collectible_images')
            .update({ order: i })
            .eq('id', img.existingId);

          if (orderError) throw orderError;
        }
      }

      // Delete removed images
      const existingImageIds = images
        .filter(img => img.type === 'url')
        .map(img => img.existingId)
        .filter(Boolean) as string[];

      if (existingImageIds.length > 0) {
        const { error: deleteError } = await supabase
          .from('collectible_images')
          .delete()
          .eq('collectible_id', id)
          .filter('id', 'not.in', `(${existingImageIds.join(',')})`);

        if (deleteError) throw deleteError;
      } else {
        const { error: deleteError } = await supabase
          .from('collectible_images')
          .delete()
          .eq('collectible_id', id);

        if (deleteError) throw deleteError;
      }

      // Upload new images with correct order
      const newImages = images.filter(img => img.type === 'file' && img.file);
      const startIndex = existingImages.length;

      const imagePromises = newImages.map(async (image, index) => {
        if (!image.file) return;

        const fileName = `${id}/${Date.now()}-${index}`;
        const { error: uploadError } = await supabase.storage
          .from('collectible-images')
          .upload(fileName, image.file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('collectible-images')
          .getPublicUrl(fileName);

        return supabase
          .from('collectible_images')
          .insert({
            collectible_id: id,
            image_url: publicUrl,
            order: startIndex + index
          });
      });

      await Promise.all(imagePromises);
      navigate(`/${formData.slug}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update collectible');
      setSaving(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>, useCamera: boolean = false) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remainingSlots = 6 - images.length;
    const filesToProcess = files.slice(0, remainingSlots);

    for (const file of filesToProcess) {
      const reader = new FileReader();
      
      reader.onloadend = () => {
        const imageData = {
          id: Math.random().toString(36).substring(7),
          url: reader.result as string,
          file,
          type: 'file' as const
        };

        setImages(prev => [...prev, imageData]);
      };

      reader.readAsDataURL(file);
    }

    if (useCamera) {
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    } else {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImage = (id: string) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  const moveImage = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= images.length) return;

    setImages(prev => {
      const newImages = [...prev];
      [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
      return newImages;
    });
  };

  const handleImageEdit = (editedImage: string) => {
    if (selectedImageIndex === null) return;

    const byteString = atob(editedImage.split(',')[1]);
    const mimeString = editedImage.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const file = new File([ab], `edited-image-${Date.now()}.jpg`, { type: mimeString });

    setImages(prev => {
      const newImages = [...prev];
      newImages[selectedImageIndex] = {
        id: Math.random().toString(36).substring(7),
        url: editedImage,
        file,
        type: 'file'
      };
      return newImages;
    });

    setShowImageEditor(false);
    setSelectedImageIndex(null);
  };

  const handleCameraCapture = (imageData: string) => {
    const byteString = atob(imageData.split(',')[1]);
    const mimeString = imageData.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const file = new File([ab], `camera-image-${Date.now()}.jpg`, { type: mimeString });

    const newImageData = {
      id: Math.random().toString(36).substring(7),
      url: imageData,
      file,
      type: 'file' as const
    };

    setImages(prev => [...prev, newImageData]);
    setShowCamera(false);
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to view this page.</p>
          <button
            onClick={() => navigate('/')}
            className="text-primary-600 hover:text-primary-800 font-medium"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-gradient-to-b from-gray-50 to-white rounded-2xl shadow-xl p-8 border border-gray-100">
        {/* Header with action buttons */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 pb-6 border-b border-gray-100">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="h-12 w-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-100">
                <Edit2 className="h-6 w-6 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 h-3 w-3 bg-primary-400 rounded-full border-2 border-white animate-pulse" />
            </div>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent">
                Edit Listing
              </h1>
              <p className="text-gray-500">Update your collectible details</p>
            </div>
          </div>
          
          {/* Action buttons - hidden on mobile, shown at bottom */}
          <div className="hidden sm:flex items-center gap-3">
            <button
              type="button"
              onClick={retryAnalysis}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Sparkles className="h-4 w-4" />
              Rewrite with AI
            </button>
            <button
              type="button"
              onClick={() => navigate(`/${formData.slug}`)}
              className="px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="edit-form"
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-primary-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </div>
        )}

        <form id="edit-form" onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="coin">Coin</option>
                <option value="note">Note</option>
              </select>
            </div>

            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="available">Available</option>
                <option value="sold">Sold</option>
                <option value="unavailable">Unavailable</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="category_id" className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                id="category_id"
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
                required
                className="w-full rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              >
                <option value="">Select a category</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                Price (₹)
              </label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                required
                min="0"
                className="w-full rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                placeholder="Enter price in INR"
              />
            </div>
          </div>

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleNameChange}
              required
              className="w-full rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="Enter a descriptive title"
            />
          </div>

          <div>
            <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
              URL Slug
            </label>
            <input
              type="text"
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={handleSlugChange}
              required
              className="w-full rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="unique-url-friendly-identifier"
            />
          </div>

          <div>
            <label htmlFor="youtube_url" className="block text-sm font-medium text-gray-700 mb-1">
              YouTube Video URL (optional)
            </label>
            <div className="relative">
              <input
                type="url"
                id="youtube_url"
                name="youtube_url"
                value={formData.youtube_url}
                onChange={handleChange}
                pattern="^https?:\/\/(www\.)?(youtube\.com\/(watch\?v=|shorts\/)|youtu\.be\/)[a-zA-Z0-9_-]{11}$"
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-gray-300 shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                placeholder="e.g., https://youtube.com/watch?v=..."
              />
              <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Add a YouTube video showcasing the collectible. Must be a valid YouTube video URL.
            </p>
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={6}
              className="w-full rounded-xl border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="Describe the item in detail including condition, authenticity, historical significance, and any other relevant details"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700">Images</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Add up to 6 high-quality images
                </p>
              </div>
              
              {/* Mobile-only AI rewrite button */}
              <div className="sm:hidden">
                <button
                  type="button"
                  onClick={retryAnalysis}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  AI Rewrite
                </button>
              </div>
            </div>

            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              multiple
              onChange={(e) => handleFileSelect(e, false)}
            />

            <input
              type="file"
              ref={cameraInputRef}
              className="hidden"
              accept="image/*"
              capture="environment"
              onChange={(e) => handleFileSelect(e, true)}
            />

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
              {images.map((image, index) => (
                <div
                  key={image.id}
                  className="aspect-square relative group rounded-lg overflow-hidden bg-gray-100 border-2 border-transparent hover:border-primary-300 transition-colors"
                >
                  <div className="absolute top-1 left-1 z-10 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                    {index + 1}
                  </div>
                  <img
                    src={image.url}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="flex flex-col gap-2">
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => moveImage(index, 'up')}
                          disabled={index === 0}
                          className="p-1.5 bg-white rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move left"
                        >
                          <ChevronUp className="h-4 w-4 rotate-[-90deg]" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveImage(index, 'down')}
                          disabled={index === images.length - 1}
                          className="p-1.5 bg-white rounded-lg text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move right"
                        >
                          <ChevronDown className="h-4 w-4 rotate-[90deg]" />
                        </button>
                      </div>
                      <div className="flex gap-1 justify-center">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedImageIndex(index);
                            setShowImageEditor(true);
                          }}
                          className="p-1.5 bg-white rounded-lg text-primary-600 hover:text-primary-700 transition-colors"
                          title="Edit image"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeImage(image.id)}
                          className="p-1.5 bg-white rounded-lg text-red-500 hover:text-red-700 transition-colors"
                          title="Remove image"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {images.length < 6 && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      if (cameraInputRef.current) {
                        cameraInputRef.current.click();
                      }
                    }}
                    className="aspect-square flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all"
                  >
                    <Camera className="h-6 w-6 text-gray-400" />
                    <span className="text-sm text-gray-600">Use Camera</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      if (fileInputRef.current) {
                        fileInputRef.current.click();
                      }
                    }}
                    className="aspect-square flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-all"
                  >
                    <ImageIcon className="h-6 w-6 text-gray-400" />
                    <span className="text-sm text-gray-600">Upload</span>
                  </button>
                </>
              )}
            </div>
          </div>
          
          {/* Mobile action buttons - fixed at bottom */}
          <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex gap-3 z-10">
            <button
              type="button"
              onClick={() => navigate(`/${formData.slug}`)}
              className="flex-1 bg-white border border-gray-200 text-gray-700 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors shadow-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-primary-600 text-white px-4 py-3 rounded-xl hover:bg-primary-700 transition-colors disabled:bg-primary-300 disabled:cursor-not-allowed shadow-sm font-medium flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
          
          {/* Add padding at the bottom on mobile to account for fixed buttons */}
          <div className="sm:hidden h-20"></div>
        </form>
      </div>

      {showImageEditor && selectedImageIndex !== null && (
        <ImageEditor
          imageUrl={images[selectedImageIndex].url}
          onSave={handleImageEdit}
          onClose={() => {
            setShowImageEditor(false);
            setSelectedImageIndex(null);
          }}
        />
      )}

      {showCamera && (
        <CameraCapture
          onCapture={handleCameraCapture}
          onClose={() => setShowCamera(false)}
        />
      )}
    </div>
  );
}

export default EditCollectible;